import type { ArtworkRequestDetailResponse } from "@hops/sdk/types";
import type React from "react";
import { StatusBadge } from "./StatusBadge";

interface RequestDetailProps {
  request: ArtworkRequestDetailResponse;
  onStartWork?: () => void;
  onUpdateStatus?: () => void;
  onPublish?: () => void;
  onBack?: () => void;
  isWorking?: boolean;
  canStartWork?: boolean;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function ReadonlyField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: "var(--panel-spacing-sm)" }}>
      <div
        style={{
          fontSize: "var(--panel-font-size-xs)",
          color: "var(--panel-text-muted)",
          fontWeight: 500,
          marginBottom: 1,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "var(--panel-font-size-md)" }}>
        {value || "—"}
      </div>
    </div>
  );
}

export function RequestDetail({
  request,
  onStartWork,
  onUpdateStatus,
  onPublish,
  onBack,
  isWorking: _isWorking,
  canStartWork,
}: RequestDetailProps) {
  const isInProgress = request.statusCode === "INPR";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "var(--panel-spacing-sm) var(--panel-spacing-md)",
          borderBottom: "1px solid var(--panel-border)",
        }}
      >
        {onBack && (
          <button
            className="hops-btn hops-btn-ghost hops-btn-sm"
            onClick={onBack}
            style={{ padding: "var(--panel-spacing-xs)", marginRight: 8 }}
            data-testid="panel-back-button"
          >
            ←
          </button>
        )}
        <span
          style={{ fontWeight: 600, marginRight: 8 }}
          data-testid="panel-request-id"
        >
          {request.requestId ?? `#${request.id}`}
        </span>
        <StatusBadge
          statusCode={request.statusCode}
          statusDescription={request.statusDescription}
        />
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "var(--panel-spacing-md)",
        }}
      >
        {/* Order info */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0 var(--panel-spacing-md)",
          }}
        >
          <ReadonlyField label="Trans ID" value={request.transId} />
          <ReadonlyField label="Entry #" value={request.entryNum} />
          <ReadonlyField
            label="Request Date"
            value={formatDate(request.requestDate)}
          />
          <ReadonlyField
            label="Needed By"
            value={formatDate(request.neededByDate)}
          />
        </div>

        {/* Customer */}
        <div
          style={{
            borderTop: "1px solid var(--panel-border)",
            paddingTop: "var(--panel-spacing-sm)",
            marginTop: "var(--panel-spacing-xs)",
          }}
        >
          <ReadonlyField label="Customer" value={request.custOrgName} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 var(--panel-spacing-md)",
            }}
          >
            <ReadonlyField label="OSAS ID" value={request.custOsasId} />
            <ReadonlyField label="State" value={request.custState} />
          </div>
        </div>

        {/* Product */}
        <div
          style={{
            borderTop: "1px solid var(--panel-border)",
            paddingTop: "var(--panel-spacing-sm)",
            marginTop: "var(--panel-spacing-xs)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 var(--panel-spacing-md)",
            }}
          >
            <ReadonlyField label="Part ID" value={request.partId} />
            <ReadonlyField label="Die Size" value={request.dieSize} />
            <ReadonlyField label="Type" value={request.requestType} />
            <ReadonlyField label="Division" value={request.division} />
          </div>
          <ReadonlyField label="Product" value={request.productName} />
        </div>

        {/* Description */}
        {request.artDescr && (
          <div
            style={{
              borderTop: "1px solid var(--panel-border)",
              paddingTop: "var(--panel-spacing-sm)",
              marginTop: "var(--panel-spacing-xs)",
            }}
          >
            <ReadonlyField label="Art Description" value={request.artDescr} />
          </div>
        )}

        {/* Notes */}
        {request.creationNotes && (
          <ReadonlyField label="Notes" value={request.creationNotes} />
        )}

        {/* Reference images */}
        {request.images && request.images.length > 0 && (
          <div
            style={{
              borderTop: "1px solid var(--panel-border)",
              paddingTop: "var(--panel-spacing-sm)",
              marginTop: "var(--panel-spacing-xs)",
            }}
          >
            <div
              style={{
                fontSize: "var(--panel-font-size-xs)",
                color: "var(--panel-text-muted)",
                fontWeight: 500,
                marginBottom: "var(--panel-spacing-xs)",
              }}
            >
              Reference Images ({request.images.length})
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              {request.images.map((img) => (
                <div
                  key={String(img.id)}
                  style={{
                    width: 48,
                    height: 48,
                    margin: "0 4px 4px 0",
                    borderRadius: "var(--panel-radius)",
                    border: "1px solid var(--panel-border)",
                    overflow: "hidden",
                    background: "var(--panel-surface)",
                  }}
                >
                  {img.customerImageThumb ? (
                    <img
                      src={img.customerImageThumb}
                      alt={img.customerImageName ?? "Reference"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "var(--panel-font-size-xs)",
                        color: "var(--panel-text-muted)",
                      }}
                    >
                      IMG
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assignment */}
        <div
          style={{
            borderTop: "1px solid var(--panel-border)",
            paddingTop: "var(--panel-spacing-sm)",
            marginTop: "var(--panel-spacing-xs)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 var(--panel-spacing-md)",
            }}
          >
            <ReadonlyField
              label="Artist"
              value={request.assignedToFullName ?? request.artPerson}
            />
            <ReadonlyField label="CSR" value={request.csrName} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          padding: "var(--panel-spacing-sm) var(--panel-spacing-md)",
          borderTop: "1px solid var(--panel-border)",
          display: "flex",
        }}
      >
        {canStartWork && !isInProgress && (
          <button
            className="hops-btn hops-btn-primary hops-btn-full"
            onClick={onStartWork}
            data-testid="panel-start-work"
          >
            Start Work
          </button>
        )}

        {isInProgress && onPublish && (
          <button
            className="hops-btn hops-btn-primary hops-btn-full"
            onClick={onPublish}
            data-testid="panel-publish"
          >
            Publish Artwork
          </button>
        )}

        {onUpdateStatus && (
          <button
            className="hops-btn hops-btn-secondary"
            onClick={onUpdateStatus}
            style={{ flexShrink: 0, marginLeft: 8 }}
            data-testid="panel-status-button"
          >
            Status
          </button>
        )}
      </div>
    </div>
  );
}
